import { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LabelOffOutlinedIcon from '@mui/icons-material/LabelOffOutlined';
import type { Theme } from '@mui/material/styles';
import type { ProjectTrack, TrackItemDependency } from '../../api/types';
import {
  buildGanttModel,
  toDayNumber,
  type GanttBar,
  type GanttItemRow,
  type GanttMilestoneInput,
  type GanttMilestoneRow,
  type GanttModel,
  type GanttRow,
  type GanttScheduleLinkInput,
  type Pace,
} from './model';

/** A pinned detail card: which row it describes, its content, and where to anchor it. */
interface GanttSelection {
  title: string;
  lines: string[];
  /** Anchor point in timeline-SVG coordinates (scrolls with the bars). */
  anchorX: number;
  anchorY: number;
}

/** Human-readable pace/status label for the tap-to-inspect detail card. */
function paceLabel(pace: Pace, status: string): string {
  if (pace === 'late') return 'Late';
  if (pace === 'behind') return 'Behind pace';
  const s = (status ?? '').replace(/_/g, ' ').trim();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : 'On pace';
}

const ROW_H = 40;
const HEADER_H = 30;
const LABEL_W = 280;
const DAY_PX = 26;
const BAR_H = 16;
const GHOST_H = 5;
/** Milestone diamond half-size (px); also the connector's attach offset off the centre. */
const MS_DIAMOND_R = 6;
/** Cosmetic gutter (px) inset on each bar so contiguous bars don't touch — and so
 *  finish-to-start arrows get a little forward room to elbow down-then-right. */
const BAR_GAP = 12;
const MS_PER_DAY = 86_400_000;

interface GanttChartProps {
  tracks: ProjectTrack[];
  dependencies?: TrackItemDependency[];
  /** Critical-path item ids (PR2 feeds this; empty in PR1). */
  criticalPath?: string[];
  /** Project non-working days (ISO), shaded as columns. */
  holidays?: string[];
  /** Milestones rendered as ◆ markers/rows at the top (e.g. PO, DP, FAT). */
  milestones?: GanttMilestoneInput[];
  /** Polymorphic milestone↔node schedule links, drawn as arrows alongside item deps. */
  milestoneLinks?: GanttScheduleLinkInput[];
  /** Injectable "now" for deterministic rendering/tests. */
  now?: number;
  /**
   * Cap the chart's visible height so it scrolls internally (both axes) and the
   * horizontal scrollbar stays on screen instead of sitting below a tall list.
   * Any CSS height (e.g. '70vh', 600). Omit for full height (print/report).
   */
  maxHeight?: number | string;
}

function dayLabel(rangeStartDay: number, offset: number): string {
  const d = new Date((rangeStartDay + offset) * MS_PER_DAY);
  return new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: 'short' }).format(d);
}

/**
 * Finish-to-start connector from a predecessor's finish (startX,startY) to a
 * successor's start (endX,endY).
 *
 *  - Spacious (successor starts well after the predecessor's finish): forward
 *    elbow — right, down, right.
 *  - Tight / contiguous (no forward room — the successor starts on or just after
 *    the predecessor's finish, so endX ≈ startX): drop STRAIGHT DOWN from the
 *    predecessor's finish, then turn right into the successor. This replaces the
 *    old backward S-jog (right, down, LEFT, down, right). A short guaranteed
 *    rightward run keeps the arrowhead pointing forward even when the bars are
 *    flush (endX === startX), where it enters a touch into the successor bar.
 */
function elbowPath(startX: number, startY: number, endX: number, endY: number): string {
  const stub = 10;
  if (endX >= startX + 2 * stub) {
    return `M ${startX} ${startY} H ${endX - stub} V ${endY} H ${endX}`;
  }
  // Drop straight down from the predecessor's finish, then go right to land
  // EXACTLY on the successor's left edge (endX) — never inside the bar, never a
  // backward jog. The arrowhead sits at the edge pointing right (the markers use
  // a fixed rightward orient, so the head reads correctly even when this final
  // run is zero-length for perfectly flush bars).
  return `M ${startX} ${startY} V ${endY} H ${endX}`;
}

/** Comfortable horizontal run for a milestone connector's final approach (px). */
const MS_LEAD = 16;

/**
 * Milestone connector path. A milestone usually sits on the SAME date as the item
 * it drives, leaving only the bar inset (~BAR_GAP/2) of forward room — too little
 * to read as an elbow. So for a forward target that's closer than MS_LEAD, turn
 * MS_LEAD before it: the connector drops clear of the diamond and its guide line,
 * then runs horizontally into the arrowhead with real breathing room. Roomier
 * targets (and backward gates) fall back to the shared {@link elbowPath}.
 */
function milestoneElbow(fromX: number, fromY: number, toX: number, toY: number): string {
  if (toX > fromX && toX - fromX < MS_LEAD) {
    const turnX = toX - MS_LEAD;
    // Drop clear of the diamond BEFORE turning left, so the connector doesn't hug
    // the diamond's lower-left corner and crowd it — give the dot breathing room.
    const clearY = fromY + MS_DIAMOND_R + 4;
    return `M ${fromX} ${fromY} V ${clearY} H ${turnX} V ${toY} H ${toX}`;
  }
  return elbowPath(fromX, fromY, toX, toY);
}

export function GanttChart({
  tracks,
  dependencies = [],
  criticalPath = [],
  holidays = [],
  milestones = [],
  milestoneLinks = [],
  now,
  maxHeight,
}: GanttChartProps) {
  const theme = useTheme();
  // On phones the 280px sticky name column crowds out the bars, so default to
  // collapsed (bars take the full width) and let the user toggle names back on.
  // Desktop is unaffected — names always show, no toggle.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showLabelsOverride, setShowLabelsOverride] = useState<boolean | null>(null);
  // Tap-to-inspect: touch devices have no hover, so the native <title> tooltips
  // never show. Clicking (or tapping) a row pins a detail card anchored to it;
  // clicking empty space or the card dismisses it. Desktop hover still works too.
  // Stored by stable row identity ("kind:id") so a data refresh that reorders or
  // drops rows re-anchors (or clears) the card correctly instead of pointing at
  // the wrong row.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const labelsVisible = !isMobile || (showLabelsOverride ?? false);
  const labelW = labelsVisible ? LABEL_W : 0;
  const model: GanttModel = useMemo(
    () => buildGanttModel(tracks, dependencies, criticalPath, now, milestones, milestoneLinks),
    [tracks, dependencies, criticalPath, now, milestones, milestoneLinks],
  );

  if (model.empty) {
    return (
      <Box data-testid="gantt-empty" sx={{ py: 4, textAlign: 'center' }}>
        <Typography color="text.secondary" variant="body2">
          No planned dates yet. Add planned start/end to tracks or items in the Plan tab to see the timeline.
        </Typography>
      </Box>
    );
  }

  const { rows, arrows, milestoneArrows, totalDays, rangeStartDay, todayOffset } = model;
  const chartW = totalDays * DAY_PX;
  const timelineW = chartW + 56; // trailing room for the % label after the last bar
  const svgH = HEADER_H + rows.length * ROW_H;
  // Timeline-pane x starts at 0 — the task column is a separate, sticky pane.
  const xForOffset = (offset: number) => offset * DAY_PX;
  const rowCenter = (rowIndex: number) => HEADER_H + rowIndex * ROW_H + ROW_H / 2;

  // Item id → row (for arrow endpoints).
  const itemRowById = new Map<string, GanttItemRow>();
  for (const r of rows) if (r.kind === 'item') itemRowById.set(r.id, r);
  // Milestone id → row (for milestone↔node arrow endpoints).
  const milestoneRowById = new Map<string, GanttMilestoneRow>();
  for (const r of rows) if (r.kind === 'milestone') milestoneRowById.set(r.id, r);

  // Weekly axis ticks.
  const ticks: number[] = [];
  for (let d = 0; d < totalDays; d += 7) ticks.push(d);

  // Holiday columns (offsets within the visible range).
  const holidayOffsets =
    rangeStartDay === null
      ? []
      : holidays
          .map((iso) => toDayNumber(iso))
          .filter((d): d is number => d !== null)
          .map((d) => d - rangeStartDay)
          .filter((offset) => offset >= 0 && offset < totalDays);

  // Inset each bar by BAR_GAP/2 on both ends → a visible gutter between flush bars.
  const barLeft = (b: GanttBar) => xForOffset(b.x0) + BAR_GAP / 2;
  const barWidth = (b: GanttBar) => Math.max(DAY_PX * 0.6, (b.x1 - b.x0) * DAY_PX - BAR_GAP);

  // Attachment point for a milestone-arrow endpoint: an item attaches at its bar's
  // right edge (predecessor) or left edge (successor); a milestone attaches at its
  // diamond. Returns null when the endpoint isn't drawable.
  const endpointPos = (
    end: { kind: 'item' | 'milestone'; id: string },
    side: 'from' | 'to',
  ): { x: number; y: number } | null => {
    if (end.kind === 'item') {
      const row = itemRowById.get(end.id);
      if (!row?.bar) return null;
      const x = side === 'from' ? barLeft(row.bar) + barWidth(row.bar) : barLeft(row.bar);
      return { x, y: rowCenter(row.rowIndex) };
    }
    const row = milestoneRowById.get(end.id);
    if (!row || row.dayOffset === null) return null;
    const cx = xForOffset(row.dayOffset);
    const cy = rowCenter(row.rowIndex);
    // SOURCE: exit the diamond's BOTTOM vertex — below the diamond body (so the
    // line doesn't start inside it) while staying at the milestone's x, which
    // leaves horizontal room into the inset successor bar so the connector elbows
    // down-then-right and meets the arrowhead head-on. TARGET: stay at the centre.
    return side === 'from' ? { x: cx, y: cy + MS_DIAMOND_R } : { x: cx, y: cy };
  };

  // Hover tooltip: inclusive date span for a bar, e.g. "2 Jul 2026 → 5 Jul 2026"
  // (x1 is exclusive). Rendered as a native <title> on the bar.
  const fmtDay = (offset: number) =>
    rangeStartDay === null
      ? ''
      : new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }).format(
          new Date((rangeStartDay + offset) * MS_PER_DAY),
        );
  const barRange = (b: GanttBar | null): string | null =>
    b ? `${fmtDay(b.x0)} → ${fmtDay(b.x1 - 1)}` : null;

  // Pace → fill color for the actual-% portion (W3): amber when the fill lags
  // today's position in the bar, red when the derived status is Late.
  const paceColor = (pace: Pace, onpace: string): string =>
    pace === 'late' ? theme.palette.error.main : pace === 'behind' ? theme.palette.warning.main : onpace;

  // Build the tap-to-inspect card contents for a row — the same facts the hover
  // <title> conveys, spelled out over multiple lines so they read on a phone.
  const rowDetail = (row: GanttRow): { title: string; lines: string[] } => {
    if (row.kind === 'milestone') {
      const date = row.dayOffset !== null && rangeStartDay !== null ? fmtDay(row.dayOffset) : '—';
      const lines = [`Date: ${date}`, `Status: ${row.done ? 'Done' : 'Pending'}`];
      if (row.movedFromDueDate) lines.push(`Moved by link (due ${row.movedFromDueDate})`);
      if (row.delayReason) lines.push(`Note: ${row.delayReason}`);
      return { title: `◆ ${row.name}`, lines };
    }
    const range = barRange(row.bar);
    const progress = `Progress: ${row.actualPct}%${row.expectedPct !== null ? ` (expected ${row.expectedPct}%)` : ''}`;
    if (row.kind === 'track') {
      const lines = [range ? `Dates: ${range}` : 'No planned dates', progress, `Status: ${paceLabel(row.pace, row.status)}`];
      if (row.unattributed) lines.push('Progress not attributed to items');
      return { title: row.label, lines };
    }
    // item
    const lines = [range ? `Dates: ${range}` : 'Unplanned', progress, `Status: ${paceLabel(row.pace, row.status)}`];
    if (row.critical) lines.push('On critical path');
    if (row.baselineBar) lines.push(`Baseline: ${fmtDay(row.baselineBar.x0)} → ${fmtDay(row.baselineBar.x1 - 1)}`);
    if (row.unattributed) lines.push('Progress not attributed to this item');
    return { title: row.name, lines };
  };

  const rowKey = (row: GanttRow) => `${row.kind}:${row.id}`;
  const anchorXFor = (row: GanttRow) =>
    row.kind === 'milestone'
      ? row.dayOffset !== null
        ? xForOffset(row.dayOffset)
        : 8
      : row.bar
        ? barLeft(row.bar)
        : 8;

  const selectRow = (row: GanttRow) => (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    // Tapping the already-selected row toggles the card off.
    setSelectedKey((prev) => (prev === rowKey(row) ? null : rowKey(row)));
  };

  // Re-derive the pinned card from current rows each render, so it re-anchors on
  // data changes and simply vanishes if its row is gone.
  const selectedRow = selectedKey ? rows.find((r) => rowKey(r) === selectedKey) ?? null : null;
  const selectedCard: GanttSelection | null = selectedRow
    ? {
        ...rowDetail(selectedRow),
        anchorX: anchorXFor(selectedRow),
        anchorY: rowCenter(selectedRow.rowIndex),
      }
    : null;

  return (
    <Paper variant="outlined" data-testid="gantt-chart">
      {/* Mobile-only: toggle the sticky name column so the bars can use the full width. */}
      {isMobile && (
        <Stack direction="row" justifyContent="flex-end" sx={{ px: 1, pt: 0.5 }}>
          <Button
            size="small"
            startIcon={labelsVisible ? <LabelOffOutlinedIcon /> : <LabelOutlinedIcon />}
            onClick={() => setShowLabelsOverride((v) => !(v ?? false))}
            aria-label="toggle track names"
          >
            {labelsVisible ? 'Hide names' : 'Show names'}
          </Button>
        </Stack>
      )}
      {/* Bounded scroll viewport: keeps the horizontal scrollbar on screen. */}
      <Box sx={{ overflow: 'auto', maxHeight }}>
        <Box sx={{ display: 'flex', width: labelW + timelineW }}>
        {/* Locked task column — stays pinned while the timeline scrolls */}
        {labelsVisible && (
        <Box
          data-testid="gantt-task-column"
          sx={{
            position: 'sticky',
            left: 0,
            zIndex: 2,
            flex: '0 0 auto',
            bgcolor: 'background.paper',
            borderRight: 1,
            borderColor: 'divider',
          }}
        >
          <svg width={LABEL_W} height={svgH} role="img" aria-label="Task names">
            <text x={8} y={HEADER_H - 10} fontSize={11} fontWeight={700} fill={theme.palette.text.secondary}>
              Task
            </text>
            {rows.map((row) => {
              const y = HEADER_H + row.rowIndex * ROW_H;
              const cy = y + ROW_H / 2;
              const labelIndent = row.kind === 'item' ? 24 : 8;
              const labelFont = row.kind === 'track' ? 13 : 12;
              const fullLabel =
                row.kind === 'track'
                  ? row.label
                  : row.kind === 'milestone'
                    ? `◆ ${row.name}`
                    : row.name;
              // Fit chars to the label-column width (~0.6em/char); long names wrap
              // onto a second line instead of being clipped with an ellipsis.
              const labelChars = Math.max(8, Math.floor((LABEL_W - labelIndent - 6) / (labelFont * 0.6)));
              const labelLines = wrapLabel(fullLabel, labelChars);
              return (
                <g key={`label-${row.kind}-${row.id}`}>
                  {/* zebra row background */}
                  <rect
                    x={0}
                    y={y}
                    width={LABEL_W}
                    height={ROW_H}
                    fill={row.rowIndex % 2 === 0 ? 'transparent' : theme.palette.action.hover}
                  />
                  {/* label — wraps to 2 lines for long names; full text on hover */}
                  <text
                    x={labelIndent}
                    fontSize={labelFont}
                    fontWeight={row.kind === 'item' ? 400 : 700}
                    fill={row.kind === 'milestone' ? theme.palette.secondary.main : theme.palette.text.primary}
                  >
                    <title>{fullLabel}</title>
                    {labelLines.length === 1 ? (
                      <tspan x={labelIndent} y={cy + 4}>
                        {labelLines[0]}
                      </tspan>
                    ) : (
                      <>
                        <tspan x={labelIndent} y={cy - 3}>
                          {labelLines[0]}
                        </tspan>
                        <tspan x={labelIndent} y={cy + 11}>
                          {labelLines[1]}
                        </tspan>
                      </>
                    )}
                  </text>
                </g>
              );
            })}
          </svg>
        </Box>
        )}

        {/* Scrolling timeline */}
        <Box sx={{ flex: '0 0 auto' }}>
          <svg
            width={timelineW}
            height={svgH}
            role="img"
            aria-label="Project timeline Gantt chart"
            onClick={() => setSelectedKey(null)}
          >
            <defs>
              {/* Fixed rightward orient (not "auto"): every connector enters the
                  successor from its LEFT edge, so the head always points right —
                  even when the final path run is vertical/zero-length. */}
              <marker
                id="gantt-arrowhead"
                markerWidth="7"
                markerHeight="7"
                refX="6"
                refY="3"
                orient="0"
              >
                <path d="M0,0 L6,3 L0,6 Z" fill={theme.palette.text.secondary} />
              </marker>
              <marker
                id="gantt-ms-arrowhead"
                markerWidth="7"
                markerHeight="7"
                refX="6"
                refY="3"
                orient="0"
              >
                <path d="M0,0 L6,3 L0,6 Z" fill={theme.palette.secondary.main} />
              </marker>
              <pattern id="gantt-hatch" width="6" height="6" patternTransform="rotate(45)" patternUnits="userSpaceOnUse">
                <rect width="6" height="6" fill={theme.palette.grey[300]} />
                <line x1="0" y1="0" x2="0" y2="6" stroke={theme.palette.grey[400]} strokeWidth="2" />
              </pattern>
            </defs>

            {/* Holiday columns (non-working days) — drawn behind rows + bars */}
            {holidayOffsets.map((offset) => (
              <rect
                key={`holiday-${offset}`}
                data-testid={`gantt-holiday-${offset}`}
                x={xForOffset(offset)}
                y={HEADER_H}
                width={DAY_PX}
                height={svgH - HEADER_H}
                fill={theme.palette.warning.light}
                opacity={0.18}
              />
            ))}

            {/* Weekly gridlines + axis labels */}
            {ticks.map((d) => (
              <g key={`tick-${d}`}>
                <line
                  x1={xForOffset(d)}
                  y1={HEADER_H}
                  x2={xForOffset(d)}
                  y2={svgH}
                  stroke={theme.palette.divider}
                  strokeWidth={1}
                />
                <text
                  x={xForOffset(d) + 2}
                  y={HEADER_H - 10}
                  fontSize={10}
                  fill={theme.palette.text.secondary}
                >
                  {rangeStartDay !== null ? dayLabel(rangeStartDay, d) : ''}
                </text>
              </g>
            ))}

            {/* Today line */}
            {todayOffset !== null && (
              <line
                data-testid="gantt-today"
                x1={xForOffset(todayOffset) + DAY_PX / 2}
                y1={HEADER_H}
                x2={xForOffset(todayOffset) + DAY_PX / 2}
                y2={svgH}
                stroke={theme.palette.error.main}
                strokeWidth={1.5}
                strokeDasharray="4 3"
              />
            )}

            {/* Milestone guide lines (faint, full height) so each ◆ relates to the phases below */}
            {rows
              .filter((r): r is GanttMilestoneRow => r.kind === 'milestone' && r.dayOffset !== null)
              .map((r) => (
                <line
                  key={`ms-line-${r.id}`}
                  x1={xForOffset(r.dayOffset as number)}
                  y1={HEADER_H}
                  x2={xForOffset(r.dayOffset as number)}
                  y2={svgH}
                  stroke={theme.palette.secondary.main}
                  strokeWidth={1}
                  strokeDasharray="2 3"
                  opacity={0.3}
                />
              ))}

            {/* Rows — bars only; labels live in the locked task column */}
            {rows.map((row) => {
              const y = HEADER_H + row.rowIndex * ROW_H;
              const cy = y + ROW_H / 2;
              return (
                <g
                  key={`bar-${row.kind}-${row.id}`}
                  onClick={selectRow(row)}
                  style={{ cursor: 'pointer' }}
                >
                  {/* zebra row background */}
                  <rect
                    x={0}
                    y={y}
                    width={timelineW}
                    height={ROW_H}
                    fill={row.rowIndex % 2 === 0 ? 'transparent' : theme.palette.action.hover}
                  />

                  {row.kind === 'milestone' ? (
                    <MilestoneMarker
                      row={row}
                      cy={cy}
                      x={row.dayOffset !== null ? xForOffset(row.dayOffset) : null}
                      dateLabel={
                        row.dayOffset !== null && rangeStartDay !== null
                          ? dayLabel(rangeStartDay, row.dayOffset)
                          : ''
                      }
                      theme={theme}
                    />
                  ) : row.kind === 'track' ? (
                    <g data-testid={`gantt-track-${row.id}`} data-actual-pct={row.actualPct} data-unattributed={row.unattributed} data-pace={row.pace}>
                      <title>
                        {`${row.label}${barRange(row.bar) ? ` · ${barRange(row.bar)}` : ''} · ${row.actualPct}%`}
                      </title>
                      {row.bar && (
                        <SummaryBar
                          x={barLeft(row.bar)}
                          w={barWidth(row.bar)}
                          cy={cy}
                          pct={row.actualPct}
                          color={paceColor(row.pace, theme.palette.primary.dark)}
                          track={theme.palette.grey[400]}
                        />
                      )}
                      {/* show the authoritative actual-% next to the summary bar */}
                      {row.bar && (
                        <text
                          x={barLeft(row.bar) + barWidth(row.bar) + 6}
                          y={cy + 4}
                          fontSize={11}
                          fill={theme.palette.text.secondary}
                        >
                          {row.actualPct}%{row.unattributed ? ' (unattributed)' : ''}
                        </text>
                      )}
                    </g>
                  ) : (
                    <ItemBars
                      row={row}
                      cy={cy}
                      barLeft={barLeft}
                      barWidth={barWidth}
                      fillColor={paceColor(row.pace, theme.palette.primary.main)}
                      theme={theme}
                      tooltip={row.bar ? `${row.name} · ${barRange(row.bar)} · ${row.actualPct}%` : null}
                    />
                  )}
                </g>
              );
            })}

            {/* Dependency arrows (finish-to-start elbows) */}
            {arrows.map((a) => {
              const from = itemRowById.get(a.fromItemId);
              const to = itemRowById.get(a.toItemId);
              if (!from?.bar || !to?.bar) return null;
              const startX = barLeft(from.bar) + barWidth(from.bar);
              const startY = rowCenter(from.rowIndex);
              const endX = barLeft(to.bar);
              const endY = rowCenter(to.rowIndex);
              const d = elbowPath(startX, startY, endX, endY);
              return (
                <path
                  key={`arrow-${a.id}`}
                  data-testid={`gantt-arrow-${a.id}`}
                  d={d}
                  fill="none"
                  stroke={theme.palette.text.secondary}
                  strokeWidth={1.3}
                  markerEnd="url(#gantt-arrowhead)"
                />
              );
            })}

            {/* Milestone↔node links (dashed, secondary color) — a milestone end attaches
                at its diamond; an item end attaches at the bar's right (from) / left (to). */}
            {milestoneArrows.map((a) => {
              const from = endpointPos(a.from, 'from');
              const to = endpointPos(a.to, 'to');
              if (!from || !to) return null;
              const d = milestoneElbow(from.x, from.y, to.x, to.y);
              return (
                <path
                  key={`ms-arrow-${a.id}`}
                  data-testid={`gantt-ms-arrow-${a.id}`}
                  d={d}
                  fill="none"
                  stroke={theme.palette.secondary.main}
                  strokeWidth={1.3}
                  strokeDasharray="5 3"
                  markerEnd="url(#gantt-ms-arrowhead)"
                />
              );
            })}

            {/* Tap-to-inspect detail card (touch has no hover) — pinned to the tapped row */}
            {selectedCard && (
              <DetailCard
                selection={selectedCard}
                timelineW={timelineW}
                svgH={svgH}
                theme={theme}
                onClose={() => setSelectedKey(null)}
              />
            )}
          </svg>
        </Box>
        </Box>
      </Box>
      <Legend />
    </Paper>
  );
}

function SummaryBar({
  x,
  w,
  cy,
  pct,
  color,
  track,
}: {
  x: number;
  w: number;
  cy: number;
  pct: number;
  color: string;
  track: string;
}) {
  const h = 8;
  const y = cy - h / 2;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={2} fill={track} />
      <rect x={x} y={y} width={(w * pct) / 100} height={h} rx={2} fill={color} />
    </g>
  );
}

function MilestoneMarker({
  row,
  cy,
  x,
  dateLabel,
  theme,
}: {
  row: GanttMilestoneRow;
  cy: number;
  x: number | null;
  dateLabel: string;
  theme: Theme;
}) {
  if (x === null) return null;
  const r = MS_DIAMOND_R;
  const color = row.done ? theme.palette.success.main : theme.palette.secondary.main;
  const tip =
    `${row.name} — ${dateLabel}` +
    (row.movedFromDueDate ? ` · moved by link (due ${row.movedFromDueDate})` : '') +
    (row.delayReason ? ` · ${row.delayReason}` : '');
  return (
    <g data-testid={`gantt-milestone-${row.id}`} data-moved={row.movedFromDueDate ?? undefined}>
      <title>{tip}</title>
      {/* diamond at the milestone date */}
      <path
        d={`M ${x} ${cy - r} L ${x + r} ${cy} L ${x} ${cy + r} L ${x - r} ${cy} Z`}
        fill={color}
        stroke={theme.palette.background.paper}
        strokeWidth={1}
      />
      <text x={x + r + 4} y={cy + 4} fontSize={10} fill={theme.palette.text.secondary}>
        {dateLabel}
      </text>
    </g>
  );
}

/**
 * Pinned detail card for the tap-to-inspect flow. Rendered inside the timeline
 * SVG (so it scrolls with the bars) and anchored to the tapped row. Placed below
 * the anchor by default, flipped above when it would overflow the bottom, and
 * clamped horizontally so it never spills past the chart edges. Tapping it (or
 * empty timeline space) dismisses it — the only affordance touch users have.
 */
function DetailCard({
  selection,
  timelineW,
  svgH,
  theme,
  onClose,
}: {
  selection: GanttSelection;
  timelineW: number;
  svgH: number;
  theme: Theme;
  onClose: () => void;
}) {
  const { title, lines, anchorX, anchorY } = selection;
  const padX = 12;
  const padY = 10;
  const titleH = 18;
  const lineH = 16;
  const closeHintH = 14;
  // Rough monospace-ish width estimate; the card is bounded so long notes wrap by
  // clamping rather than growing without limit.
  const longest = Math.max(title.length, ...lines.map((l) => l.length), 'Tap to close'.length);
  const w = Math.min(340, Math.max(150, longest * 6.6 + padX * 2));
  const h = padY * 2 + titleH + lines.length * lineH + closeHintH;

  // Clamp horizontally inside the chart; flip above the row if there's no room below.
  const cardX = Math.min(Math.max(4, anchorX), Math.max(4, timelineW - w - 4));
  const belowY = anchorY + 16;
  const cardY = belowY + h > svgH ? Math.max(4, anchorY - h - 16) : belowY;

  return (
    <g data-testid="gantt-detail-card" onClick={onClose} style={{ cursor: 'pointer' }}>
      {/* soft drop shadow */}
      <rect x={cardX + 1.5} y={cardY + 2} width={w} height={h} rx={7} fill={theme.palette.common.black} opacity={0.12} />
      <rect
        x={cardX}
        y={cardY}
        width={w}
        height={h}
        rx={7}
        fill={theme.palette.background.paper}
        stroke={theme.palette.divider}
        strokeWidth={1}
      />
      <text x={cardX + padX} y={cardY + padY + 12} fontSize={12} fontWeight={700} fill={theme.palette.text.primary}>
        {title}
      </text>
      {lines.map((line, i) => (
        <text
          key={i}
          x={cardX + padX}
          y={cardY + padY + titleH + i * lineH + 11}
          fontSize={11}
          fill={theme.palette.text.secondary}
        >
          {line}
        </text>
      ))}
      <text
        x={cardX + w - padX}
        y={cardY + h - 8}
        fontSize={10}
        textAnchor="end"
        fill={theme.palette.text.disabled}
      >
        Tap to close
      </text>
    </g>
  );
}

function ItemBars({
  row,
  cy,
  barLeft,
  barWidth,
  fillColor,
  theme,
  tooltip,
}: {
  row: GanttItemRow;
  cy: number;
  barLeft: (b: GanttBar) => number;
  barWidth: (b: GanttBar) => number;
  /** Pace-tinted color for the actual-% fill (W3). */
  fillColor: string;
  theme: Theme;
  /** Native hover tooltip ("name · dates · %"); null when the item is unplanned. */
  tooltip?: string | null;
}) {
  const y = cy - BAR_H / 2;
  return (
    <g
      data-testid={`gantt-item-${row.id}`}
      data-planned={row.planned}
      data-unattributed={row.unattributed}
      data-critical={row.critical}
      data-actual-pct={row.actualPct}
      data-pace={row.pace}
    >
      {tooltip && <title>{tooltip}</title>}
      {/* baseline ghost bar (frozen plan) under the live bar */}
      {row.baselineBar && (
        <rect
          data-testid={`gantt-baseline-${row.id}`}
          x={barLeft(row.baselineBar)}
          y={y + BAR_H + 1}
          width={barWidth(row.baselineBar)}
          height={GHOST_H}
          rx={1}
          fill={theme.palette.grey[400]}
          opacity={0.7}
        />
      )}

      {row.bar ? (
        row.unattributed ? (
          // Track has progress but this item isn't attributed → hatch, no % fill.
          <rect
            x={barLeft(row.bar)}
            y={y}
            width={barWidth(row.bar)}
            height={BAR_H}
            rx={3}
            fill="url(#gantt-hatch)"
            stroke={row.critical ? theme.palette.error.main : theme.palette.grey[400]}
            strokeWidth={row.critical ? 2 : 1}
          >
            <title>Progress logged on the track but not attributed to this item</title>
          </rect>
        ) : (
          <g>
            {/* remaining (track) */}
            <rect
              x={barLeft(row.bar)}
              y={y}
              width={barWidth(row.bar)}
              height={BAR_H}
              rx={3}
              fill={theme.palette.primary.light}
              opacity={0.45}
              stroke={row.critical ? theme.palette.error.main : 'none'}
              strokeWidth={row.critical ? 2 : 0}
            />
            {/* actual-% fill (pace-tinted: amber behind, red late) */}
            <rect
              x={barLeft(row.bar)}
              y={y}
              width={(barWidth(row.bar) * row.actualPct) / 100}
              height={BAR_H}
              rx={3}
              fill={fillColor}
            />
            {row.critical && (
              <rect
                x={barLeft(row.bar)}
                y={y}
                width={barWidth(row.bar)}
                height={BAR_H}
                rx={3}
                fill="none"
                stroke={theme.palette.error.main}
                strokeWidth={2}
              />
            )}
          </g>
        )
      ) : (
        // Unplanned: no bar, just a muted dash where the bar would be.
        <text x={4} y={cy + 4} fontSize={11} fill={theme.palette.text.disabled} fontStyle="italic">
          unplanned
        </text>
      )}
    </g>
  );
}

function Legend() {
  return (
    <Stack direction="row" gap={2} flexWrap="wrap" sx={{ px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}>
      <LegendItem color="primary.main" label="Actual %" />
      <LegendItem color="warning.main" label="Behind pace" />
      <LegendItem color="error.main" label="Late" />
      <LegendItem color="grey.400" label="Baseline (ghost)" />
      <LegendSwatch label="Critical path" sx={{ border: 2, borderColor: 'error.main' }} />
      <LegendSwatch label="Unattributed" sx={{ background: 'repeating-linear-gradient(45deg,#bbb,#bbb 2px,#eee 2px,#eee 5px)' }} />
      <LegendItem color="warning.light" label="Holiday" />
    </Stack>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <Stack direction="row" gap={0.5} alignItems="center">
      <Box sx={{ width: 14, height: 10, borderRadius: 0.5, bgcolor: color }} />
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
  );
}

function LegendSwatch({ label, sx }: { label: string; sx: object }) {
  return (
    <Stack direction="row" gap={0.5} alignItems="center">
      <Box sx={{ width: 14, height: 10, borderRadius: 0.5, ...sx }} />
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
  );
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/**
 * Split a label into at most two lines that each fit `maxChars`, breaking on a
 * word boundary. A single over-long word is hard-split; the second line is
 * ellipsised only if it still overflows. Full text stays available via <title>.
 */
function wrapLabel(s: string, maxChars: number): string[] {
  if (s.length <= maxChars) return [s];
  const words = s.split(' ');
  if (words.length === 1) return [s.slice(0, maxChars), truncate(s.slice(maxChars), maxChars)];
  let line1 = words[0];
  let i = 1;
  for (; i < words.length; i++) {
    if (`${line1} ${words[i]}`.length > maxChars) break;
    line1 = `${line1} ${words[i]}`;
  }
  const rest = words.slice(i).join(' ');
  return rest ? [line1, truncate(rest, maxChars)] : [line1];
}
